/**
 * 將 send-phone-otp / verify-phone-otp 的後端錯誤轉成使用者友善訊息。
 * 後端失敗條件（line-login.service.sendPhoneOtp）：
 *  - 429 'resend cooldown active'       → 冷卻中（以 lineUserId 為 key，見下）
 *  - 429 'daily send limit exceeded'    → 同門號每日 10 次上限
 *  - 400 'send phone otp failed'        → 簡訊供應商發送失敗
 */

/** 冷卻回應可能帶的剩餘秒數（後端 429 與冷卻查詢端點都會回）。 */
export const readRemainingSeconds = (error: unknown): number | null => {
  const raw = (
    error as { response?: { data?: { resendAvailableInSeconds?: unknown } } } | undefined
  )?.response?.data?.resendAvailableInSeconds;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : null;
};

export const getSendOtpErrorMessage = (error: unknown): string => {
  const e = error as { response?: { status?: number; data?: { message?: unknown } } } | undefined;
  const status = e?.response?.status;
  const raw = e?.response?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(" ") : typeof raw === "string" ? raw : "";
  const low = msg.toLowerCase();

  if (status === 429 && low.includes("daily")) {
    return "今日驗證碼發送次數已達上限，請明日再試或聯繫客服";
  }
  if (status === 429) {
    // 冷卻是綁「LINE 帳號」而非門號（後端以 lineUserId 為 key），所以換一支號碼再送
    // 一樣會被擋。不講清楚的話使用者會以為系統壞了——實際上這是防濫用的設計：
    // 若改綁門號，只要不斷換號碼就能無限發送付費簡訊給任意對象，每日上限（per 門號）擋不住。
    const left = readRemainingSeconds(error);
    return left
      ? `同一帳號 ${left} 秒後才能再次發送`
      : "同一帳號需間隔約 1 分鐘才能再次發送驗證碼";
  }
  if (status === 401) {
    return "登入逾時，請重新開啟頁面後再試";
  }
  return "發送驗證碼失敗，請確認手機號碼後重試";
};

/**
 * verify-phone-otp 的錯誤訊息。
 *
 * 原本所有失敗一律顯示「驗證碼錯誤，請重新輸入」，但後端至少有五種 400：
 *  - 'otp incorrect'          → 真的打錯
 *  - 'otp expired'            → 超過 5 分鐘（語音特別容易踩到：電話等得久、還要抄）
 *  - 'otp not found'          → 根本沒有有效的碼（多半是驗過一次或已過期被清掉）
 *  - 'otp attempts exceeded'  → 錯 5 次，該組碼已作廢
 *  - 'phone format invalid'   → DTO 擋下（市話曾因此永遠驗不過）
 * 全部說成「驗證碼錯誤」會叫使用者一直重打一組不可能過的碼。
 */
export const getVerifyOtpErrorMessage = (error: unknown): string => {
  const e = error as { response?: { status?: number; data?: { message?: unknown } } } | undefined;
  const raw = e?.response?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(" ") : typeof raw === "string" ? raw : "";
  const low = msg.toLowerCase();

  if (low.includes("attempts exceeded")) {
    return "錯誤次數過多，這組驗證碼已失效，請重新取得驗證碼";
  }
  if (low.includes("expired") || low.includes("not found")) {
    return "驗證碼已逾時失效，請重新取得驗證碼";
  }
  if (low.includes("phone") && (low.includes("mismatch") || low.includes("format"))) {
    return "號碼與取得驗證碼時不符，請重新取得驗證碼";
  }
  if (e?.response?.status === 401) {
    return "登入逾時，請重新開啟頁面後再試";
  }
  return "驗證碼錯誤，請重新輸入";
};
